/**
 * Dropstack audio engine: gentle generative music, quiet ambience and soft
 * SFX, fully synthesized with WebAudio (docs/AUDIO.md, GAME_DESIGN.md §10).
 *
 * Lifecycle & safety:
 *  - Nothing is created until unlock() is called from a user gesture
 *    (autoplay policy). Before that — and whenever WebAudio is unavailable
 *    (SSR, tests, old browsers) — every method is a safe no-op that only
 *    remembers state (theme, volumes, intensity, music wanted, paused).
 *  - Audio pauses (fade → scheduler stop → context suspend) when the game
 *    pauses or the tab is hidden, and resumes smoothly.
 *  - Every public method is guarded: an audio failure never breaks the game.
 */
import type { ThemeAudio, ThemePack } from '@dropstack/core';
import { DEFAULT_SETTINGS, type AudioEngine, type AudioSettings, type SfxName } from '../contracts.ts';
import { AmbiencePlayer } from './ambiencePlayer.ts';
import { applyIntensity, applyVolumes, createMixGraph, duckMusic, fadePause, type MixGraph } from './graph.ts';
import { clamp01, intensityLayerGain } from './mix.ts';
import { MUSIC_STOP_FADE_S, MusicPlayer, THEME_CROSSFADE_S } from './musicPlayer.ts';
import { REGISTER, rootMidi } from './pitch.ts';
import { SfxRateLimiter } from './rateLimit.ts';
import { SCHEDULER_INTERVAL_MS, SCHEDULE_AHEAD_S } from './scheduler.ts';
import { SFX } from './sfxSpecs.ts';
import { SfxPlayer } from './sfxPlayer.ts';
import { DEFAULT_THEME_AUDIO, sanitizeThemeAudio } from './timbres.ts';

/** Where tab visibility comes from (the document in the browser). */
export interface VisibilitySource {
  readonly visibilityState: DocumentVisibilityState;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

export interface AudioEngineOptions {
  /** AudioContext factory; defaults to the browser's (webkit)AudioContext. Return null when unavailable. */
  createContext?: () => AudioContext | null;
  /** Visibility source for auto-pause; defaults to `document` when there is one. */
  visibility?: VisibilitySource | null;
}

export interface DisposableAudioEngine extends AudioEngine {
  /** Stop everything, close the AudioContext and remove listeners. */
  dispose(): void;
}

const PAUSE_FADE_S = 0.25;
const RESUME_FADE_S = 0.35;
/** Suspend the context (saves battery) once the pause fade has finished. */
const SUSPEND_DELAY_MS = (PAUSE_FADE_S + 0.1) * 1000;
/** Music starts this far after startMusic() so its first notes are never late. */
const MUSIC_START_DELAY_S = 0.05;
/** setIntensity() changes smaller than this are ignored (the game may call it every frame). */
const INTENSITY_EPSILON = 0.005;

function browserContext(): AudioContext | null {
  const g = globalThis as typeof globalThis & { webkitAudioContext?: typeof AudioContext };
  const Ctor = typeof g.AudioContext === 'function' ? g.AudioContext : g.webkitAudioContext;
  if (typeof Ctor !== 'function') return null;
  try {
    return new Ctor({ latencyHint: 'interactive' });
  } catch {
    try {
      return new Ctor();
    } catch {
      return null;
    }
  }
}

function browserVisibility(): VisibilitySource | null {
  return typeof document === 'undefined' ? null : document;
}

function sanitizeSettings(v: AudioSettings | null | undefined): AudioSettings {
  const d = DEFAULT_SETTINGS.audio;
  if (!v) return { ...d };
  const level = (x: unknown, fallback: number): number => (typeof x === 'number' ? clamp01(x) : fallback);
  return { master: level(v.master, d.master), music: level(v.music, d.music), sfx: level(v.sfx, d.sfx), muted: v.muted === true };
}

interface Runtime {
  ctx: AudioContext;
  graph: MixGraph;
  music: MusicPlayer;
  ambience: AmbiencePlayer;
  sfx: SfxPlayer;
}

const noop = (): void => {};

export function createAudioEngine(options: AudioEngineOptions = {}): DisposableAudioEngine {
  const createContext = options.createContext ?? browserContext;
  const visibility = options.visibility === undefined ? browserVisibility() : options.visibility;

  // State remembered before unlock (and applied on unlock).
  let settings = sanitizeSettings(DEFAULT_SETTINGS.audio);
  let theme: ThemeAudio = { ...DEFAULT_THEME_AUDIO };
  let sfxRoot = rootMidi(theme.key, REGISTER.sfx);
  let intensity = 0;
  let musicWanted = false;
  let userPaused = false;
  let hidden = visibility?.visibilityState === 'hidden';

  let rt: Runtime | null = null;
  let unavailable = false;
  let disposed = false;
  let pauseApplied = false;
  /** True while a resume() requested by unlock() (i.e. from a user gesture) is settling. */
  let gestureResumePending = false;
  let timer: ReturnType<typeof setInterval> | null = null;
  let suspendTimer: ReturnType<typeof setTimeout> | null = null;
  const rateLimiter = new SfxRateLimiter<SfxName>(SFX);

  let warned = false;
  function guard<A extends unknown[]>(fn: (...args: A) => void): (...args: A) => void {
    return (...args: A) => {
      try {
        fn(...args);
      } catch (err) {
        if (!warned) {
          warned = true;
          console.warn('[audio] ignored an audio error:', err);
        }
      }
    };
  }

  const isPaused = (): boolean => userPaused || hidden;

  // ───────────── Scheduler ─────────────

  function tick(): void {
    if (!rt) return;
    const now = rt.ctx.currentTime;
    const until = now + SCHEDULE_AHEAD_S;
    rt.music.tick(now, until, intensityLayerGain(intensity));
    rt.ambience.tick(now, until);
    if (!rt.music.active && !rt.ambience.active) syncTimer();
  }
  const guardedTick = guard(tick);

  /** Run the lookahead timer only while something needs scheduling. */
  function syncTimer(): void {
    const needed = rt !== null && !isPaused() && (rt.music.active || rt.ambience.active);
    if (needed && timer === null) timer = setInterval(guardedTick, SCHEDULER_INTERVAL_MS);
    if (!needed && timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  }

  function startMusicNow(): void {
    if (!rt || !musicWanted || isPaused() || rt.music.playing) return;
    const at = rt.ctx.currentTime + MUSIC_START_DELAY_S;
    rt.music.start(theme, at);
    rt.ambience.switchTo(theme.ambience, at, THEME_CROSSFADE_S);
    syncTimer();
    tick();
  }

  // ───────────── Pause ─────────────

  function clearSuspendTimer(): void {
    if (suspendTimer !== null) {
      clearTimeout(suspendTimer);
      suspendTimer = null;
    }
  }

  function syncPause(): void {
    if (!rt) return;
    const paused = isPaused();
    if (paused === pauseApplied) return;
    pauseApplied = paused;
    clearSuspendTimer();
    const { ctx } = rt;
    if (paused) {
      fadePause(rt.graph, true, PAUSE_FADE_S);
      syncTimer();
      suspendTimer = setTimeout(() => {
        suspendTimer = null;
        if (isPaused() && ctx.state === 'running') ctx.suspend().catch(noop);
      }, SUSPEND_DELAY_MS);
    } else {
      if (ctx.state !== 'running') ctx.resume().catch(noop);
      fadePause(rt.graph, false, RESUME_FADE_S);
      syncTimer();
      startMusicNow();
      tick();
    }
  }

  const onVisibilityChange = guard(() => {
    hidden = visibility?.visibilityState === 'hidden';
    syncPause();
  });
  visibility?.addEventListener('visibilitychange', onVisibilityChange);

  // ───────────── Unlock ─────────────

  /** iOS only un-mutes WebAudio after a sound starts inside the gesture: play one silent sample. */
  function primeOutput(ctx: AudioContext): void {
    const src = ctx.createBufferSource();
    src.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
    src.connect(ctx.destination);
    src.onended = () => src.disconnect();
    src.start(0);
  }

  /**
   * Resume from a user gesture. Until the promise settles, SFX are still
   * scheduled (they sound the moment the clock starts), so the tap that
   * unlocked audio is not lost on browsers that resume asynchronously.
   */
  function resumeFromGesture(ctx: AudioContext): Promise<void> {
    gestureResumePending = true;
    const settle = (): void => {
      gestureResumePending = false;
    };
    return ctx.resume().then(settle, settle);
  }

  function unlock(): Promise<void> {
    if (disposed || unavailable) return Promise.resolve();
    if (rt) {
      const { ctx } = rt;
      if (!isPaused() && ctx.state !== 'running' && ctx.state !== 'closed') return resumeFromGesture(ctx);
      return Promise.resolve();
    }
    // Created synchronously, inside the user gesture.
    const ctx = createContext();
    if (!ctx) {
      unavailable = true;
      return Promise.resolve();
    }
    const graph = createMixGraph(ctx);
    rt = { ctx, graph, music: new MusicPlayer(graph), ambience: new AmbiencePlayer(graph), sfx: new SfxPlayer(graph) };
    applyVolumes(graph, settings);
    applyIntensity(graph, intensity);
    primeOutput(ctx);
    pauseApplied = false;
    syncPause();
    startMusicNow();
    if (isPaused()) return Promise.resolve();
    return resumeFromGesture(ctx);
  }

  // ───────────── Public API ─────────────

  const engine: DisposableAudioEngine = {
    unlock: () => {
      try {
        return unlock();
      } catch (err) {
        unavailable = true;
        console.warn('[audio] WebAudio unavailable:', err);
        return Promise.resolve();
      }
    },

    setTheme: guard((pack: ThemePack) => {
      theme = sanitizeThemeAudio(pack?.audio);
      sfxRoot = rootMidi(theme.key, REGISTER.sfx);
      if (!rt || !rt.music.playing) return;
      const at = rt.music.setTheme(theme);
      rt.ambience.switchTo(theme.ambience, at, THEME_CROSSFADE_S);
      syncTimer();
    }),

    startMusic: guard(() => {
      musicWanted = true;
      startMusicNow();
    }),

    stopMusic: guard(() => {
      musicWanted = false;
      if (!rt) return;
      rt.music.stop();
      rt.ambience.stop(MUSIC_STOP_FADE_S);
    }),

    play: guard((name: SfxName) => {
      if (!rt || isPaused() || settings.muted || settings.master <= 0 || settings.sfx <= 0) return;
      if (!Object.hasOwn(SFX, name)) return;
      if (rt.ctx.state === 'closed') return;
      if (rt.ctx.state !== 'running' && !gestureResumePending) {
        // The OS or browser suspended the context behind our back (iOS
        // interruption, a resume() refused outside a gesture). SFX usually
        // come from a tap, so this is a good moment to try again; this one
        // sound is dropped rather than queued to burst out later.
        rt.ctx.resume().catch(noop);
        return;
      }
      if (!rateLimiter.allow(name, rt.ctx.currentTime * 1000)) return;
      rt.sfx.play(name, sfxRoot);
      if (SFX[name].ducksMusic && rt.music.active) duckMusic(rt.graph);
    }),

    setVolumes: guard((v: AudioSettings) => {
      settings = sanitizeSettings(v);
      if (rt) applyVolumes(rt.graph, settings);
    }),

    setIntensity: guard((x: number) => {
      const next = clamp01(x);
      // Skip no-op updates, but always let the exact ends (0 and 1) through.
      const negligible = Math.abs(next - intensity) < INTENSITY_EPSILON && next > 0 && next < 1;
      if (next === intensity || negligible) return;
      intensity = next;
      if (rt) applyIntensity(rt.graph, intensity);
    }),

    setPaused: guard((paused: boolean) => {
      userPaused = paused === true;
      syncPause();
    }),

    dispose: guard(() => {
      if (disposed) return;
      disposed = true;
      visibility?.removeEventListener('visibilitychange', onVisibilityChange);
      clearSuspendTimer();
      if (timer !== null) clearInterval(timer);
      timer = null;
      if (!rt) return;
      rt.music.dispose();
      rt.ambience.dispose();
      rt.ctx.close().catch(noop);
      rt = null;
    }),
  };
  return engine;
}
